import { Tab } from "@/types/webStyle.ts";
import type { UserStoreRef } from "@/stores/user.ts";

// 路由级代码分割：使用动态 import 实现按需加载
const HomeView = () => import("@/views/HomeView.vue");
const View404 = () => import("@/views/View404.vue");
const LoginView = () => import("@/views/user/LoginView.vue");
const RegisterView = () => import("@/views/user/RegisterView.vue");
const UserView = () => import("@/views/user/UserView.vue");
const UserModify = () => import("@/views/user/UserModify.vue");
const MessageView = () => import("@/views/message/MessageView.vue");

import type { RouteRecordRaw } from "vue-router";

const tabsConfig: Tab[] = [
  {
    name: "home",
    path: "/",
    title: "首页",
    showSidebar: false,
    disableGenerateTab: true,
    component: HomeView,
  },
  {
    name: "problem",
    path: "/problem",
    title: "题目",
    icon: "calculation",
    showSidebar: true,
    redirect: "problem-list",
  },
  {
    name: "collection-detail-tab",
    title: "题集",
    path: "/collection",
    showSidebar: true,
    disableGenerateTab: true,
    redirect: "problem-collection-list",
  },
  {
    name: "bot",
    path: "/bot",
    title: "对战",
    icon: "gamepad",
    showSidebar: true,
    redirect: "bot-game-list",
  },
  {
    name: "contest",
    path: "/contest",
    title: "比赛",
    icon: "application",
    showSidebar: true,
    redirect: "contest-list",
  },
  {
    name: "contest-detail-tab",
    title: "比赛",
    path: "/contest",
    showSidebar: true,
    disableGenerateTab: true,
    redirect: "contest-list",
  },
  {
    name: "discuss",
    title: "讨论",
    path: "/discuss",
    icon: "sonic",
    showSidebar: true,
    redirect: "discuss-list",
  },
  {
    name: "discuss-detail-tab",
    title: "讨论",
    path: "/discuss",
    showSidebar: true,
    disableGenerateTab: true,
    redirect: "discuss-list",
  },
  {
    name: "rank",
    path: "/rank",
    title: "排名",
    icon: "chart-column",
    showSidebar: true,
    redirect: "rank-ac-all",
  },
  {
    name: "study",
    path: "/study",
    title: "研习",
    icon: "book-open",
    showSidebar: true,
    redirect: "study-view",
  },
  {
    // AI 教练：浮窗全站可用（快捷入口），本 tab 承载教练主界面（会话列表 + 对话区）
    name: "ai",
    path: "/ai",
    title: "AI 教练",
    icon: "chat-bubble",
    showSidebar: true,
    needLogin: true,
    redirect: "ai-conversation",
  },
  {
    name: "system",
    path: "/system",
    title: "系统",
    icon: "dashboard",
    showSidebar: true,
    redirect: "system-status",
  },
  {
    // 「管理」是一个**容器 tab**：下面每个子页各自带权限点（题目/比赛/评测/站点/权限…），
    // 因此这里刻意**不设** tab 级门禁。
    //
    // 原先这里挂着 `auths: [ManageWeb]`，后果不只是「少一个入口」——parseViewRouter 会把它
    // 写进每个子页的路由 meta，router 守卫再与子页自己的 auths 取 AND，于是：
    //
    //     一个只有 i-manage-problem 的助教，既看不到「管理」入口，
    //     直接访问 /manage/problem/list 也会被 403 —— 他手里的权限完全用不了。
    //
    // 容器的门禁只该决定「入口显不显示」，不该决定「子页能不能开」；后者由子页自己的
    // auths 负责（路由守卫与侧边栏菜单都按它过滤）。而入口显不显示改为**按子页推导**：
    // 一个子页都进不去时整个 tab 不显示（见 view-config.ts 的 getVisibleTabs）。
    // 这样「新增一个管理子页」不需要回来改这里，也不会漏改。
    name: "manage",
    path: "/manage",
    title: "管理",
    icon: "command",
    showSidebar: true,
    redirect: "manage-web",
  },
  {
    name: "login",
    path: "/login",
    title: "登陆",
    showSidebar: false,
    disableGenerateTab: true,
    component: LoginView,
  },
  {
    name: "register",
    path: "/register",
    title: "注册",
    showSidebar: false,
    disableGenerateTab: true,
    component: RegisterView,
  },

  {
    name: "user",
    path: "/user/:username+",
    title: "用户",
    showSidebar: false,
    disableGenerateTab: true,
    component: UserView,
  },
  {
    name: "user-modify",
    path: "/user",
    title: "用户设置",
    showSidebar: false,
    disableGenerateTab: true,
    component: UserModify,
  },
  {
    name: "message",
    path: "/message",
    title: "私信",
    showSidebar: false,
    disableGenerateTab: true,
    needLogin: true,
    component: MessageView,
  },
  {
    name: "404",
    path: "/404",
    title: "404",
    showSidebar: false,
    disableGenerateTab: true,
    component: View404,
  },
];

export const getTabConfig = (tabName: string) => {
  return tabsConfig.find((tab) => tab.name == tabName);
};

export const getGenerateTabs = (userStore: UserStoreRef) => {
  const tabs = [] as Tab[];
  tabsConfig.forEach((tab) => {
    if (!tab.disableGenerateTab && userStore.hasAllAuths(tab.auths ?? [])) {
      tabs.push(tab);
    }
  });
  return tabs;
};

export const makeTabRouter = (routers: RouteRecordRaw[]) => {
  tabsConfig.forEach((tab) => {
    const router = {
      name: tab.name,
      path: tab.path,
      meta: {
        title: tab.title,
        tab: tab.name,
        showSidebar: tab.showSidebar,
        needLogin: tab.needLogin,
        redirect: tab.redirect,
        tabAuths: tab.auths,
      },
      component: tab.component,
    } as RouteRecordRaw;

    routers.push(router);
  });
};
